import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Empty, ErrorState, Field, Loading, Modal, Select, TextInput, useToast } from '../../components/ui';
import { api, download } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { date, dateTime } from '../../lib/format';

/**
 * The data principal request register, and acting on one guest's rights:
 * a copy of their data, a correction, an erasure.
 */

interface DataRequest {
  id: string;
  reference: string;
  requestType: string;
  status: string;
  verificationStatus: string;
  subjectType: string;
  subjectId: string | null;
  subjectLabel: string | null;
  contact: string | null;
  channel: string | null;
  submittedAt: string;
  dueAt: string | null;
  overdue: boolean;
  completedAt: string | null;
  assignedTo: string | null;
  resolution: string | null;
  notes: string | null;
}

const TYPE_LABELS: Record<string, string> = {
  ACCESS: 'Copy of data',
  CORRECTION: 'Correction',
  ERASURE: 'Erasure',
  CONSENT_WITHDRAWAL: 'Withdraw consent',
  GRIEVANCE: 'Grievance',
  OTHER: 'Other',
};

const NEXT: Record<string, string[]> = {
  SUBMITTED: ['IDENTITY_VERIFICATION', 'UNDER_REVIEW', 'REJECTED', 'CANCELLED'],
  IDENTITY_VERIFICATION: ['UNDER_REVIEW', 'REJECTED', 'CANCELLED'],
  UNDER_REVIEW: ['APPROVED', 'REJECTED', 'CANCELLED'],
  APPROVED: ['PROCESSING', 'COMPLETED', 'CANCELLED'],
  PROCESSING: ['COMPLETED', 'CANCELLED'],
};

export function RequestsTab() {
  const { can } = useAuth();
  const [openOnly, setOpenOnly] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<DataRequest | null>(null);
  const requests = useQuery({
    queryKey: ['privacy-requests', openOnly],
    queryFn: () => api.get<DataRequest[]>(`/api/privacy/requests${openOnly ? '?open=true' : ''}`),
  });

  return (
    <div className="card">
      <div className="card-head row between">
        <h3>Data requests</h3>
        <div className="row gap-6">
          <label className="row gap-6 small">
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
            Open only
          </label>
          {can('privacy:manage') ? (
            <button className="btn primary sm" onClick={() => setCreating(true)}>
              Record a request
            </button>
          ) : null}
        </div>
      </div>
      <div className="card-body small muted">
        Requests from guests about their data - a copy, a correction, an erasure, withdrawing consent,
        or a complaint. Guests can also send one from your booking and menu pages. Verify who is asking
        before acting on it.
      </div>
      {requests.isLoading ? (
        <Loading />
      ) : requests.error ? (
        <ErrorState error={requests.error} onRetry={() => void requests.refetch()} />
      ) : (requests.data ?? []).length === 0 ? (
        <Empty message={openOnly ? 'No open requests.' : 'No requests yet.'} />
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-stack">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Type</th>
                <th>From</th>
                <th>Status</th>
                <th>Received</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              {requests.data!.map((row) => (
                <tr key={row.id} className="clickable" onClick={() => setSelected(row)}>
                  <td className="mono">{row.reference}</td>
                  <td>{TYPE_LABELS[row.requestType] ?? row.requestType}</td>
                  <td>{row.subjectLabel ?? '—'}</td>
                  <td>
                    <span className="chip">{row.status.replace(/_/g, ' ').toLowerCase()}</span>
                  </td>
                  <td>{date(row.submittedAt)}</td>
                  <td>
                    {row.dueAt ? (
                      <span className={row.overdue ? 'chip danger' : ''}>{date(row.dueAt)}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating ? <NewRequest onClose={() => setCreating(false)} /> : null}
      {selected ? <RequestDetail request={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}

function NewRequest({ onClose }: { onClose: () => void }) {
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [requestType, setRequestType] = useState('ACCESS');
  const [subjectLabel, setSubjectLabel] = useState('');
  const [contact, setContact] = useState('');
  const [channel, setChannel] = useState('In person');
  const [notes, setNotes] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api.post('/api/privacy/requests', { requestType, subjectType: 'GUEST', subjectLabel, contact, channel, notes }),
    onSuccess: () => {
      push('Request recorded', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['privacy-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['privacy-overview'] });
      onClose();
    },
    onError: fail,
  });
  return (
    <Modal
      title="Record a data request"
      size="narrow"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={create.isPending || !subjectLabel.trim()} onClick={() => create.mutate()}>
            Record
          </button>
        </>
      }
    >
      <div className="col gap-14">
        <Select label="What they are asking for" value={requestType} onChange={(e) => setRequestType(e.target.value)}>
          {Object.entries(TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <TextInput label="Who is asking" value={subjectLabel} onChange={(e) => setSubjectLabel(e.target.value)} />
        <TextInput label="How to reply" hint="Email or phone they gave" value={contact} onChange={(e) => setContact(e.target.value)} />
        <TextInput label="How it arrived" value={channel} onChange={(e) => setChannel(e.target.value)} />
        <TextInput label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
    </Modal>
  );
}

function RequestDetail({ request, onClose }: { request: DataRequest; onClose: () => void }) {
  const { can } = useAuth();
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [resolution, setResolution] = useState(request.resolution ?? '');
  const update = useMutation({
    mutationFn: (input: Record<string, unknown>) => api.patch<DataRequest>(`/api/privacy/requests/${request.id}`, input),
    onSuccess: () => {
      push('Updated', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['privacy-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['privacy-overview'] });
      onClose();
    },
    onError: fail,
  });
  const manage = can('privacy:manage');
  const next = NEXT[request.status] ?? [];

  return (
    <Modal title={`${request.reference} · ${TYPE_LABELS[request.requestType] ?? request.requestType}`} onClose={onClose}>
      <div className="col gap-14">
        <div className="small">
          <div>
            <b>From:</b> {request.subjectLabel ?? '—'} {request.contact ? `· ${request.contact}` : ''}
          </div>
          <div>
            <b>Received:</b> {dateTime(request.submittedAt)} {request.channel ? `via ${request.channel}` : ''}
          </div>
          <div>
            <b>Status:</b> {request.status.replace(/_/g, ' ').toLowerCase()} · identity{' '}
            {request.verificationStatus.replace(/_/g, ' ').toLowerCase()}
          </div>
          {request.notes ? (
            <div>
              <b>Notes:</b> {request.notes}
            </div>
          ) : null}
        </div>
        {manage && next.length > 0 ? (
          <>
            {request.verificationStatus === 'PENDING' ? (
              <div className="row gap-6">
                <button className="btn sm" disabled={update.isPending} onClick={() => update.mutate({ verificationStatus: 'VERIFIED' })}>
                  Identity verified
                </button>
                <button className="btn sm" disabled={update.isPending} onClick={() => update.mutate({ verificationStatus: 'FAILED' })}>
                  Could not verify
                </button>
                <button className="btn sm" disabled={update.isPending} onClick={() => update.mutate({ verificationStatus: 'NOT_REQUIRED' })}>
                  Not needed
                </button>
              </div>
            ) : null}
            <Field label="Resolution - what the requester was told" hint="Needed to complete or reject">
              <textarea className="input" rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)} />
            </Field>
            <div className="row gap-6" style={{ flexWrap: 'wrap' }}>
              {next.map((status) => (
                <button
                  key={status}
                  className={`btn sm ${status === 'COMPLETED' ? 'primary' : ''}`}
                  disabled={update.isPending}
                  onClick={() => update.mutate({ status, resolution })}
                >
                  {status.replace(/_/g, ' ').toLowerCase()}
                </button>
              ))}
            </div>
            <p className="xs muted">
              To act on it, use Guest records: export a copy, correct details, or erase.
            </p>
          </>
        ) : request.resolution ? (
          <div className="small">
            <b>Resolution:</b> {request.resolution}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

interface GuestRow {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
}

export function GuestRecordsTab() {
  const { can } = useAuth();
  const { push, fail } = useToast();
  const [q, setQ] = useState('');
  const [correcting, setCorrecting] = useState<GuestRow | null>(null);
  const [erasing, setErasing] = useState<GuestRow | null>(null);
  const guests = useQuery({
    queryKey: ['privacy-guest-search', q],
    queryFn: () => api.get<GuestRow[]>(`/api/guests?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 2,
  });

  const exportGuest = async (guest: GuestRow) => {
    try {
      await download(`/api/privacy/guests/${guest.id}/export`, `guest-data-${guest.id.slice(0, 8)}.json`);
      push('Export downloaded - give it to the guest and delete your copy', 'ok');
    } catch (error) {
      fail(error);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <h3>Guest records</h3>
      </div>
      <div className="card-body col gap-6">
        <TextInput label="Find a guest" placeholder="Name or phone" value={q} onChange={(e) => setQ(e.target.value)} />
        <p className="xs muted" style={{ margin: 0 }}>
          Exports and erasures are recorded in the audit log by who did them and when - never with the
          guest's details.
        </p>
      </div>
      {q.trim().length < 2 ? null : guests.isLoading ? (
        <Loading />
      ) : (guests.data ?? []).length === 0 ? (
        <Empty message="No guest matches." />
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-stack">
            <thead>
              <tr>
                <th>Guest</th>
                <th>Phone</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {guests.data!.map((guest) => (
                <tr key={guest.id}>
                  <td>{guest.name}</td>
                  <td>{guest.phone ?? '—'}</td>
                  <td>
                    {can('privacy:manage') ? (
                      <div className="row gap-6">
                        <button className="btn sm" onClick={() => void exportGuest(guest)}>
                          Export
                        </button>
                        <button className="btn sm" onClick={() => setCorrecting(guest)}>
                          Correct
                        </button>
                        <button className="btn sm" onClick={() => setErasing(guest)}>
                          Erase
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {correcting ? <CorrectGuest guest={correcting} onClose={() => setCorrecting(null)} /> : null}
      {erasing ? <EraseGuest guest={erasing} onClose={() => setErasing(null)} /> : null}
    </div>
  );
}

function CorrectGuest({ guest, onClose }: { guest: GuestRow; onClose: () => void }) {
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState(guest.name);
  const [phone, setPhone] = useState(guest.phone ?? '');
  const [email, setEmail] = useState(guest.email ?? '');
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/privacy/guests/${guest.id}`, {
        ...(name !== guest.name ? { name } : {}),
        ...(phone !== (guest.phone ?? '') ? { phone } : {}),
        ...(email !== (guest.email ?? '') ? { email } : {}),
        reason,
      }),
    onSuccess: () => {
      push('Corrected', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['privacy-guest-search'] });
      onClose();
    },
    onError: fail,
  });
  return (
    <Modal
      title="Correct guest details"
      size="narrow"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={save.isPending || reason.trim().length < 3} onClick={() => save.mutate()}>
            Save correction
          </button>
        </>
      }
    >
      <div className="col gap-14">
        <TextInput label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <TextInput label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <TextInput label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <TextInput label="Why" hint="Recorded in the audit log" value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
    </Modal>
  );
}

function EraseGuest({ guest, onClose }: { guest: GuestRow; onClose: () => void }) {
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const erase = useMutation({
    mutationFn: () => api.post(`/api/privacy/guests/${guest.id}/erase`, { reason }),
    onSuccess: () => {
      push('Erased, except invoices already issued, which the law requires to be kept.', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['privacy-guest-search'] });
      onClose();
    },
    onError: fail,
  });
  return (
    <Modal
      title="Erase this guest's details"
      size="narrow"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn danger"
            disabled={erase.isPending || reason.trim().length < 3 || confirm !== 'ERASE'}
            onClick={() => erase.mutate()}
          >
            Erase
          </button>
        </>
      }
    >
      <div className="col gap-14 small">
        <p style={{ margin: 0 }}>
          Erases {guest.name}&apos;s name, phone, email, address, GSTIN and ID number, and their details on
          linked bookings and reservations. Their stays are kept (by id only). Invoices already issued are
          retained as tax records with the name and GSTIN printed on them, only for as long as the law requires.
          This cannot be undone.
        </p>
        <p className="muted" style={{ margin: 0 }}>
          Copies in backups taken before today stay until those backups expire.
        </p>
        <TextInput label="Why" hint="Recorded in the audit log" value={reason} onChange={(e) => setReason(e.target.value)} />
        <TextInput label="Type ERASE to confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </div>
    </Modal>
  );
}
